import { FormProvider, useForm } from 'react-hook-form';
import { ControlledDigitsField } from '@/web/components/Fields/ControlledDigitsField/ControlledDigitsField';
import { Alert, Button, Flex, Heading, Text } from '@coverflex-tech/hypernova';
import { zodResolver } from '@hookform/resolvers/zod';
import { TCodeFormSchema, codeFormSchema } from './CodeForm.schema';

export interface IProps {
  title: string;
  titleLevel?: 1 | 2 | 3 | 4;
  subtitle?: string;
  label: string;
  secondaryActionLabel: string;
  primaryActionLabel: string;
  initialCode?: string;
  error?: string;
  onChange: (code: string) => void;
  onChangeFocus?: (focusedInput: number) => void;
  onBack: () => void;
  onConfirm: (code: string) => Promise<void> | void;
}

export const CodeForm = ({
  title,
  titleLevel = 3,
  subtitle,
  label,
  secondaryActionLabel,
  primaryActionLabel,
  initialCode,
  error,
  onChange,
  onChangeFocus,
  onBack,
  onConfirm,
}: IProps) => {
  const methods = useForm<TCodeFormSchema>({
    mode: 'onTouched',
    defaultValues: {
      code: initialCode ?? '',
    },
    resolver: zodResolver(codeFormSchema),
  });

  const onSubmit = async ({ code }: TCodeFormSchema) => {
    await onConfirm(code);
  };

  const { formState } = methods;

  return (
    <Flex flexDirection="column" gap="2xl">
      <Flex gap="md" alignItems="center" justifyContent="space-between">
        <Heading level={titleLevel}>{title}</Heading>
        {subtitle && <Text>{subtitle}</Text>}
      </Flex>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Flex flexDirection="column" gap="lg">
            <ControlledDigitsField
              autofocus
              name="code"
              numberOfDigits={4}
              label={label}
              transform={(value: string) => {
                onChange(value);
                return value;
              }}
              onChangeFocus={(fields) => {
                onChangeFocus?.(fields.indexOf(true));
              }}
            />
            {error && (
              <Alert type="error" size="small">
                {error}
              </Alert>
            )}
            <Flex gap="lg">
              <Button
                variant="secondary"
                onClick={onBack}
                disabled={formState.isSubmitting}
              >
                {secondaryActionLabel}
              </Button>
              <Button
                type="submit"
                loading={formState.isSubmitting}
                disabled={!formState.isValid}
              >
                {primaryActionLabel}
              </Button>
            </Flex>
          </Flex>
        </form>
      </FormProvider>
    </Flex>
  );
};
